import React, { useEffect, useState } from "react";
import SendIcon from "@material-ui/icons/Send";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import "./Chatbot.scss";

import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_CHATBOT_RESPONSE,
  SAVE_CHAT_BOT_CHAT,
} from "../sysGuardConstants";
import DynamicTable from "./DynamicTable";
import BulletPointsView from "./BulletPointsView";

function Chatbot() {
  const dispatch = useDispatch();
  const loader = useSelector((state) => state.SysGuardReducer.chatbotLoader);
  const chatbotResponse = useSelector(
    (state) => state.SysGuardReducer.chatbotResponse
  );

  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [closing, setClosing] = useState(false);
  const [corner] = useState("bottom-right");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [hasUnread, setHasUnread] = useState(false); 

  const togglePopup = () => {
    setOpen(!open);
    if (!open) {
      // user just opened the popup
      setHasUnread(false); // clear unread indicator
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setMaximized(false);
    }, 200); // matches animation duration
  };

  const TypingIndicator = () => (
    <div className="typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );

  const MAX_USER_MESSAGES = 20;
  const handleSend = () => {
    if (!input.trim()) return;

    const userMessagesCount = messages.filter((m) => m.from === "user").length;

    if (userMessagesCount >= MAX_USER_MESSAGES) {
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: "You have finished your quota of messages. Please try again later.",
        },
      ]);
      setInput("");
      return;
    }

    setMessages([...messages, { from: "user", text: input }]);
    setInput("");
  };

  // dispatch API call when user sends message
  useEffect(() => {
    if (messages.length === 0) return;
    if (messages[messages.length - 1].from !== "user") return;

    dispatch({
      type: FETCH_CHATBOT_RESPONSE,
      payload: { query: messages[messages.length - 1].text },
    });
  }, [messages]);

  // handle API response
  useEffect(() => {
    const saveChatLogs = () => {
      if (messages.length === 0) return;

      const chatLogs = [];
      let currentEntry = {};

      messages.forEach((msg) => {
        const time = new Date().toISOString();

        if (msg.from === "user") {
          currentEntry.user = msg.text || "";
          currentEntry.time = time;
        } else if (msg.from === "bot") {
          currentEntry.bot = {
            querySummary: msg.querySummary || "",
            queryResults: msg.queryResults || [],
            suggestions: msg.suggestions || [],
          };
          if (!currentEntry.time) currentEntry.time = time;
          chatLogs.push(currentEntry);
          currentEntry = {};
        }
      });

      if (Object.keys(currentEntry).length > 0) {
        chatLogs.push(currentEntry);
      }

      const jsonString = JSON.stringify(chatLogs, null, 2);

      dispatch({
        type: SAVE_CHAT_BOT_CHAT,
        payload: { jsonString },
      });
    };

    const handleBeforeUnload = (e) => {
      saveChatLogs();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const originalLogout = window.logout;
    window.logout = () => {
      saveChatLogs();
      if (originalLogout) originalLogout();
    };

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.logout = originalLogout;
    };
  }, [messages, dispatch]);


  useEffect(() => {
    if (chatbotResponse?.query_summary) {
      const {
        query_summary,
        query_results = [],
        suggestions = [],
      } = chatbotResponse;

      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          querySummary: query_summary,
          queryResults: query_results,
          suggestions: suggestions.slice(0, 3),
        },
      ]);

      // show unread dot if popup closed
      if (!open) {
        setHasUnread(true);
      }
    }
  }, [chatbotResponse]);

  const renderWelcomeMessage = () => {
    if (messages.filter((m) => m.from === "user").length >= MAX_USER_MESSAGES) {
      return "You have reached the maximum number of messages.";
    } else {
      return "Hello! I’m your DruvStarAI.";
    }
  };

  return (
    <>
      <div
        id="chatbot-circle"
        className={`${corner} ${open ? "active" : ""} ${
          hasUnread ? "has-unread" : ""
        }`}
        onClick={togglePopup}
      >
        <img
          className="chatbot-icon"
          src="/sysGuardIcons/aiStar.png"
          alt="Chatbot Icon"
        />
        {hasUnread && <span className="unread-dot" />}
      </div>

      {open && (
        <div className={`chatbot-wrapper ${corner}`}>
          <div className={`chat-tip ${corner}`} />

          <div
            className={`chatbot-popup ${closing ? "closing" : "open"} ${
              maximized ? "maximized" : ""
            }`}
          >
            {/* Top action bar */}
            <div className="chatbot-footer">
              <button
                onClick={() => setMaximized(!maximized)}
                className="icon-btn"
                title={maximized ? "Shrink" : "Expand"}
              >
                {maximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>

              <button className="close-btn" onClick={handleClose} title="Close">
                ✖
              </button>
            </div>

            {/* Messages */}
            <div className="chatbot-body">
              {[
                ...(loader
                  ? [...messages, { from: "bot", text: "__typing__" }]
                  : messages),
              ]
                .reverse()
                .map((msg, i) => (
                  <div
                    key={i}
                    className={`chat-message-wrapper ${
                      msg.from === "user" ? "from-user" : "from-bot"
                    }`}
                  >
                    {msg.text === "__typing__" ? (
                      <div className="chat-message from-bot">
                        <TypingIndicator />
                      </div>
                    ) : msg.from === "bot" &&
                      (msg.querySummary || msg.queryResults) ? (
                      <div className="chat-message from-bot">
                        {msg.querySummary && (
                          <div className="query-summary">
                            {msg.querySummary}
                          </div>
                        )}

                        {Array.isArray(msg.queryResults) &&
                          msg.queryResults.length > 0 && (
                            <div className="query-results">
                              {msg.queryResults.length === 1 ? (
                                <BulletPointsView data={msg.queryResults[0]} />
                              ) : (
                                <DynamicTable data={msg.queryResults} />
                              )}
                            </div>
                          )}

                        {msg.suggestions && msg.suggestions.length > 0 && (
                          <div className="suggestions-box">
                            {msg.suggestions.map((s, idx) => {
                              const cleanSuggestion = s.replace(
                                /^\d+\.\s*/,
                                ""
                              );
                              return (
                                <div
                                  key={idx}
                                  className="suggestion-item"
                                  onClick={() => {
                                    setMessages((prev) => [
                                      ...prev,
                                      { from: "user", text: cleanSuggestion },
                                    ]);
                                  }}
                                >
                                  {cleanSuggestion}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`chat-message ${
                          msg.from === "user" ? "from-user" : "from-bot"
                        }`}
                      >
                        {msg.text}
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {/* Input Area */}
            <div className="chatbot-input-area">
              <input
                type="text"
                placeholder={renderWelcomeMessage()}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                disabled={
                  messages.filter((m) => m.from === "user").length >=
                  MAX_USER_MESSAGES
                }
              />
              <button
                className="send-btn"
                onClick={handleSend}
                disabled={
                  loader ||
                  messages.filter((m) => m.from === "user").length >=
                    MAX_USER_MESSAGES
                }
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;
